import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressCard, faArrowUpRightFromSquare, faCopy, faFlag, faPenToSquare, faThumbtack } from "@fortawesome/free-solid-svg-icons";

import Alert from "../../crmpostventa-shared/components/Alert";
import ChoiceGroup from "../../crmpostventa-shared/components/ChoiceGroup";
import { PRIORIDAD_LABELS, codeOf, formatCuit, formatDate, formatTime, labelFor } from "../../crmpostventa-shared/crmFormat";
import { altasBajasHref, fichaHref, isPlainClick } from "../../crmpostventa-shared/crmLinks";
import { EstadoCell, IpcStatusChip, Muted, OrigenChip, PrioridadCell, ProximoContactoCell, TipoChip } from "../components/ipcCells";
import { isMine, isProactiva, motivoLabel } from "../ipcFormat";
import { OBSERVACION_MAX, toInputDateTime } from "../nuevaGestion/nuevaGestionIpcModel";
import {
  ACCIONES,
  CANAL_OPTIONS,
  MOTIVOS_CIERRE,
  PRIORIDAD_OPTIONS,
  RESULTADO_CIERRE_OPTIONS,
  RESULTADO_CONTACTO_OPTIONS,
  accionCopy,
  cuitDigits,
  describeTimelineEntry,
  errorId,
  estadoChange,
  fieldId,
  finalizarErrorId,
  finalizarFieldId,
  motivoLinks,
  observacionObligatoria,
  proximoContactoObligatorio,
  timelineWhen,
} from "./gestionarIpcModel";

const COPY_RESET_MS = 1800;

// ---- Form field: label with * or (opcional), control, then the error or the help text. Radio groups use a span
// label that the group points at.
function Field({ id, errorKeyId, label, required = false, optional = false, full = false, group = false, error, help, children }) {
  const LabelTag = group ? "span" : "label";
  return (
    <div className={`crm-rollo__fld${full ? " crm-rollo__fld--full" : ""}${error ? " has-error" : ""}`}>
      <LabelTag className="crm-rollo__fld-label" htmlFor={group ? undefined : id} id={group ? `${id}-label` : undefined}>
        {label}
        {required ? (
          <span className="crm-rollo__req" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
        {optional ? <span className="crm-rollo__opt"> (opcional)</span> : null}
      </LabelTag>
      {children}
      {error ? (
        <span className="crm-rollo__err" id={errorKeyId}>
          {error}
        </span>
      ) : help ? (
        <span className="crm-rollo__help">{help}</span>
      ) : null}
    </div>
  );
}

const invalidProps = (error, describedById) => (error ? { "aria-invalid": true, "aria-describedby": describedById } : {});

const dateTimeText = (value) => {
  const time = formatTime(value);
  return time ? `${formatDate(value)} ${time}` : formatDate(value);
};

// ---- Header data under the comercio name (Prompt 4 "Cabecera"). Responsible actions follow D25 and the detail
// permissions: Tomar without responsible, Liberar on your own, Asignar… for anyone who can manage.
export function GestionHeader({ gestion, today, currentUserId, ownership, busy, onTomar, onLiberar, onAsignar }) {
  const numero = gestion?.numeroComercio ? ` · N° ${gestion.numeroComercio}` : "";
  const assigned = gestion?.responsableUsuarioId !== null && gestion?.responsableUsuarioId !== undefined;
  const mine = isMine(gestion, currentUserId);
  const actions = [
    ownership?.tomar ? { key: "tomar", label: "Tomar", onClick: onTomar } : null,
    ownership?.liberar ? { key: "liberar", label: "Liberar", onClick: onLiberar } : null,
    ownership?.asignar ? { key: "asignar", label: "Asignar…", onClick: onAsignar } : null,
  ].filter(Boolean);

  return (
    <>
      <span className="crm-rollo__s">
        {`CUIT ${formatCuit(gestion?.cuit)}${numero}`}
        <OrigenChip origen={gestion?.origen} />
      </span>
      <dl className="crm-rollo__facts crm-ipc__facts">
        <div>
          <dt>{isProactiva(gestion) ? "Categoría y motivo" : "Tipo y motivo"}</dt>
          <dd className="crm-ipc__tipo-motivo">
            <TipoChip item={gestion} />
            <span>{motivoLabel(gestion)}</span>
          </dd>
        </div>
        <div>
          <dt>Estado</dt>
          <dd>
            <EstadoCell item={gestion} />
          </dd>
        </div>
        <div>
          <dt>Prioridad</dt>
          <dd>
            <PrioridadCell item={gestion} />
          </dd>
        </div>
        <div>
          <dt>Responsable</dt>
          <dd className="crm-ipc__responsable">
            {assigned ? `${gestion.responsableNombre || `Usuario ${gestion.responsableUsuarioId}`}${mine ? " (vos)" : ""}` : <Muted>Sin asignar</Muted>}
            {actions.length ? (
              <span className="crm-ipc__resp-actions">
                {actions.map((action) => (
                  <button key={action.key} type="button" className="crm-rollo__link" disabled={busy} onClick={action.onClick}>
                    {action.label}
                  </button>
                ))}
              </span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Próximo contacto</dt>
          <dd>{gestion?.fechaProximoContacto ? <ProximoContactoCell item={gestion} today={today} /> : <Muted>Sin próximo contacto</Muted>}</dd>
        </div>
      </dl>
    </>
  );
}

// ---- "Copiar CUIT": clipboard first, then the legacy copy command; says when neither works (the CUIT stays visible).
// It shows the formatted CUIT and copies its digits, which is how Altas/Bajas searches it.
const copyText = async (value) => {
  if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    await navigator.clipboard.writeText(value);
    return;
  }
  if (typeof document.execCommand !== "function") throw new Error("Clipboard not available");
  const area = document.createElement("textarea");
  area.value = value;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  const copied = document.execCommand("copy");
  area.remove();
  if (!copied) throw new Error("Copy command failed");
};

function CopyCuitButton({ cuit, value }) {
  const [state, setState] = useState("idle");
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const copy = async () => {
    let next = "copied";
    try {
      await copyText(value);
    } catch (copyError) {
      next = "failed";
    }
    setState(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState("idle"), COPY_RESET_MS);
  };

  const label = { idle: "Copiar CUIT", copied: "Copiado", failed: "No se pudo copiar" }[state];
  return (
    <button
      type="button"
      className="crm-rollo__btn crm-rollo__btn--ghost crm-rollo__btn--sm"
      aria-label={state === "idle" ? `Copiar CUIT ${cuit}` : undefined}
      disabled={state !== "idle"}
      onClick={copy}
    >
      <FontAwesomeIcon icon={faCopy} aria-hidden="true" />
      {label}
    </button>
  );
}

function AppLink({ href, icon, label, onNavigate }) {
  return (
    <a
      className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm"
      href={href}
      onClick={(event) => {
        if (!onNavigate || !isPlainClick(event)) return;
        event.preventDefault();
        onNavigate(href);
      }}
    >
      <FontAwesomeIcon icon={icon} aria-hidden="true" />
      {label}
    </a>
  );
}

/**
 * Links band under the header (Prompt 7, N8; desktop and mobile): "Ver ficha" always; on an open Modificación that can
 * be managed, "Editar datos" (Ficha with the editor and the gestión) and "Ir a Altas/Bajas" with the CUIT help by
 * motive (the CUIT with "Copiar CUIT", or the name when the gestión has no CUIT). Every link is built by the common
 * link helper and is a real link (new tab, "Atrás").
 */
export function LinksBand({ gestion, editable, onNavigate }) {
  const ficha = fichaHref({ clienteId: gestion?.clienteId });
  const extra = motivoLinks(gestion, editable);
  const digits = cuitDigits(gestion?.cuit);
  return (
    <div className="crm-ipc__links" role="group" aria-label="Enlaces a otras pantallas">
      {ficha ? (
        <AppLink href={ficha} icon={faAddressCard} label="Ver ficha" onNavigate={onNavigate} />
      ) : (
        <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled title="La gestión no tiene una terminal asociada">
          <FontAwesomeIcon icon={faAddressCard} aria-hidden="true" />
          Ver ficha
        </button>
      )}
      {ficha && extra.includes("editar") ? (
        <AppLink
          href={fichaHref({ clienteId: gestion.clienteId, gestionId: gestion.id, editar: true })}
          icon={faPenToSquare}
          label="Editar datos"
          onNavigate={onNavigate}
        />
      ) : null}
      {extra.includes("altas") ? (
        <div className="crm-ipc__link-item">
          <AppLink href={altasBajasHref({ clienteId: gestion.clienteId })} icon={faArrowUpRightFromSquare} label="Ir a Altas/Bajas" onNavigate={onNavigate} />
          <div className="crm-ipc__link-help">
            {digits ? (
              <>
                <span>{`Buscá el comercio en Altas/Bajas: CUIT ${formatCuit(gestion.cuit)}`}</span>
                <CopyCuitButton cuit={formatCuit(gestion.cuit)} value={digits} />
              </>
            ) : (
              <span>Buscá el comercio en Altas/Bajas por su nombre.</span>
            )}
          </div>
          {codeOf(gestion.motivoCodigo) === "TITULARIDAD" ? (
            <div className="crm-ipc__link-help">El cambio de titular se hace con una baja y un alta.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---- Actions of the current state as RadioCards, and Finalizar as a separate link (Prompt 4).
export function ActionCards({ actions, value, estado, disabled, onChange, onFinalizar }) {
  return (
    <section className="crm-ipc__actions" aria-labelledby="crm-ipc-gestionar-acciones">
      <h3 id="crm-ipc-gestionar-acciones">¿Qué querés hacer?</h3>
      <fieldset className="crm-rollo__rcards crm-ipc__rcards" disabled={disabled}>
        <legend className="crm-rollo__sr-only">Acción</legend>
        {actions.map((accion) => {
          const copy = accionCopy(accion, estado);
          const checked = value === accion;
          return (
            <label key={accion} className={`crm-rollo__rcard${checked ? " is-selected" : ""}`}>
              <input type="radio" name="crm-ipc-gestionar-accion" value={accion} checked={checked} onChange={() => onChange(accion)} />
              <span>
                <span className="crm-ipc__rcard-title">{copy.title}</span>
                <span className="crm-rollo__s">{copy.desc}</span>
              </span>
            </label>
          );
        })}
      </fieldset>
      <div className="crm-ipc__finalize-row">
        <span className="crm-rollo__help">¿Ya está resuelta o no corresponde seguirla?</span>
        <button type="button" className="crm-rollo__link" disabled={disabled} onClick={onFinalizar}>
          <FontAwesomeIcon icon={faFlag} aria-hidden="true" />
          Finalizar gestión…
        </button>
      </div>
    </section>
  );
}

function ObservacionField({ accion, label, placeholder, value, error, onChange }) {
  const required = observacionObligatoria(accion);
  const id = fieldId("observacion");
  return (
    <Field id={id} errorKeyId={errorId("observacion")} label={label} required={required} optional={!required} full error={error}>
      <textarea
        className="crm-rollo__input crm-rollo__textarea"
        id={id}
        rows={3}
        maxLength={OBSERVACION_MAX}
        placeholder={placeholder}
        value={value}
        aria-required={required || undefined}
        onChange={(event) => onChange(event.target.value)}
        {...invalidProps(error, errorId("observacion"))}
      />
    </Field>
  );
}

function ProximoContactoField({ accion, label, gestion, value, error, now, onChange }) {
  const required = proximoContactoObligatorio(accion);
  const id = fieldId("proximoContacto");
  const actual = gestion?.fechaProximoContacto ? dateTimeText(gestion.fechaProximoContacto) : "sin próximo contacto";
  return (
    <Field id={id} errorKeyId={errorId("proximoContacto")} label={label} required={required} optional={!required} error={error} help={`Actual: ${actual}`}>
      <input
        className="crm-rollo__input"
        type="datetime-local"
        id={id}
        min={toInputDateTime(now)}
        value={value}
        aria-required={required || undefined}
        onChange={(event) => onChange(event.target.value)}
        {...invalidProps(error, errorId("proximoContacto"))}
      />
    </Field>
  );
}

function ChoiceField({ name, label, options, value, error, onChange }) {
  const id = fieldId(name);
  return (
    <Field id={id} errorKeyId={errorId(name)} label={label} required full group error={error}>
      <ChoiceGroup
        id={id}
        name={id}
        options={options}
        value={value}
        onChange={onChange}
        labelledBy={`${id}-label`}
        describedBy={error ? errorId(name) : undefined}
        invalid={Boolean(error)}
      />
    </Field>
  );
}

const Help = ({ before, strong, after }) => (
  <div className="crm-rollo__fld--full crm-rollo__help">
    {before}
    <b>{strong}</b>
    {after}
  </div>
);

/**
 * Form of the chosen action (Prompt 4 "Acciones y su formulario"). The fieldset is disabled while saving.
 */
export function ActionForm({ accion, gestion, form, errors, saving, now, onChange }) {
  const copy = accionCopy(accion, gestion?.estado);
  const set = (key) => (value) => onChange(key, value);
  const observacion = (label, placeholder) => (
    <ObservacionField accion={accion} label={label} placeholder={placeholder} value={form.observacion} error={errors.observacion} onChange={set("observacion")} />
  );
  const proximo = (label = "Próximo contacto") => (
    <ProximoContactoField
      accion={accion}
      label={label}
      gestion={gestion}
      value={form.proximoContacto}
      error={errors.proximoContacto}
      now={now}
      onChange={set("proximoContacto")}
    />
  );

  let fields = null;
  switch (accion) {
    case ACCIONES.REGISTRAR_CONTACTO:
      fields = (
        <>
          <ChoiceField name="canal" label="Canal" options={CANAL_OPTIONS} value={form.canal} error={errors.canal} onChange={set("canal")} />
          <ChoiceField name="resultado" label="Resultado" options={RESULTADO_CONTACTO_OPTIONS} value={form.resultado} error={errors.resultado} onChange={set("resultado")} />
          {codeOf(gestion?.estado) === "PENDIENTE" && form.resultado === "CONTESTO" ? (
            <div className="crm-rollo__fld--full">
              <Alert tone="info" title="Pasará a En gestión" />
            </div>
          ) : null}
          {observacion("Observación", "Qué hablaron")}
          {proximo("Reprogramar próximo contacto")}
        </>
      );
      break;
    case ACCIONES.PEDIR_DATOS:
      fields = (
        <>
          {observacion("Qué falta", "Ej.: constancia de CBU firmada por el titular")}
          {proximo()}
          <Help before="Pasa a " strong="Esperando al comercio" after=": no se marca como demorada mientras espera." />
        </>
      );
      break;
    case ACCIONES.DERIVAR:
      fields = (
        <>
          {observacion("A quién o a qué área se derivó", "Ej.: Soporte técnico, con el detalle del caso")}
          {proximo()}
          <Help before="Pasa a " strong="Derivada" after=": le toca a otra área." />
        </>
      );
      break;
    case ACCIONES.RETOMAR:
      fields = (
        <>
          {observacion("Observación", codeOf(gestion?.estado) === "DERIVADA" ? "Qué resolvió el área" : "Qué respondió el comercio")}
          <div className="crm-rollo__fld--full crm-rollo__help">Vuelve a En gestión: le toca al asesor.</div>
        </>
      );
      break;
    case ACCIONES.REPROGRAMAR:
      fields = (
        <>
          {proximo("Nuevo próximo contacto")}
          {observacion("Observación", "Por qué se reprograma")}
        </>
      );
      break;
    case ACCIONES.OBSERVAR:
      fields = observacion("Observación", "La nota queda en la línea de tiempo");
      break;
    case ACCIONES.CAMBIAR_PRIORIDAD: {
      const id = fieldId("prioridad");
      const actual = `${labelFor(PRIORIDAD_LABELS, gestion?.prioridad)} (${gestion?.prioridadManual ? "fijada a mano" : "automática"})`;
      fields = (
        <>
          <Field id={id} errorKeyId={errorId("prioridad")} label="Nueva prioridad" required error={errors.prioridad} help={`Actual: ${actual}`}>
            <span className="crm-rollo__field crm-rollo__field--select">
              <select
                className="crm-rollo__select"
                id={id}
                value={form.prioridad}
                aria-required="true"
                onChange={(event) => onChange("prioridad", event.target.value)}
                {...invalidProps(errors.prioridad, errorId("prioridad"))}
              >
                {PRIORIDAD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </span>
          </Field>
          <div className="crm-rollo__fld--full">
            <Alert tone="warn" title="Queda fijada a mano: el sistema no la va a cambiar.">
              <span className="crm-ipc__manual">
                <FontAwesomeIcon icon={faThumbtack} aria-hidden="true" />
                Aunque venza el próximo contacto, la prioridad no pasa a Crítica.
              </span>
            </Alert>
          </div>
          {observacion("Motivo del cambio", "Ej.: el comercio factura poco y puede esperar")}
        </>
      );
      break;
    }
    default:
      fields = null;
  }

  return (
    <section className="crm-rollo__action" aria-labelledby="crm-ipc-gestionar-form">
      <h3 id="crm-ipc-gestionar-form">{copy.title}</h3>
      <fieldset className="crm-rollo__form" disabled={saving}>
        <legend className="crm-rollo__sr-only">{copy.title}</legend>
        {fields}
      </fieldset>
    </section>
  );
}

// ---- Timeline (design Timeline): newest first, as the server sends it; who, when, what, the state change and the
// observation. Entries added since the previous load are highlighted.
export function Timeline({ entries, gestion, freshIds = [] }) {
  const items = entries || [];
  return (
    <>
      <h3>Línea de tiempo</h3>
      {items.length ? (
        <ol className="crm-rollo__tl">
          {items.map((entry, index) => {
            const change = estadoChange(entry);
            const classes = [index === 0 ? "is-latest" : "", freshIds.includes(entry.id) ? "is-fresh" : ""].filter(Boolean);
            return (
              <li key={entry.id ?? index} className={classes.join(" ") || undefined}>
                <div className="crm-rollo__tl-body">
                  <div className="crm-rollo__tl-when">{timelineWhen(entry)}</div>
                  {change ? (
                    <div className="crm-rollo__tl-what">
                      <span className="crm-rollo__tl-change">
                        {change.from ? (
                          <>
                            <IpcStatusChip item={{ estado: change.from }} />
                            <span className="crm-rollo__tl-arrow" aria-label="pasa a">
                              →
                            </span>
                          </>
                        ) : null}
                        <IpcStatusChip item={{ estado: change.to }} />
                      </span>
                    </div>
                  ) : null}
                  <div className="crm-rollo__tl-text">{describeTimelineEntry(entry, gestion)}</div>
                  {entry.observacion ? <div className="crm-rollo__tl-obs">{`“${entry.observacion}”`}</div> : null}
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="crm-rollo__muted">Todavía no hay movimientos.</p>
      )}
    </>
  );
}

// ---- Body of the Finalizar modal (Prompt 4): result, closing motive by result and observation (required for
// Descartada).
export function FinalizarForm({ form, errors, saving, submitError, onChange }) {
  const motivos = MOTIVOS_CIERRE[form.resultado] || [];
  const descartada = form.resultado === "DESCARTADA";
  const resultadoId = finalizarFieldId("resultado");
  const motivoId = finalizarFieldId("motivoCierre");
  const observacionId = finalizarFieldId("observacion");
  return (
    <>
      <p className="crm-rollo__confirm-text">La gestión sale de Activas y pasa a Finalizadas.</p>
      {submitError ? (
        <Alert tone="error" role="alert" title={submitError.title}>
          {submitError.text}
        </Alert>
      ) : null}
      <fieldset className="crm-rollo__form crm-rollo__form--single" disabled={saving}>
        <legend className="crm-rollo__sr-only">Finalizar gestión</legend>
        <Field id={resultadoId} errorKeyId={finalizarErrorId("resultado")} label="Resultado" required full group error={errors.resultado}>
          <ChoiceGroup
            id={resultadoId}
            name={resultadoId}
            options={RESULTADO_CIERRE_OPTIONS}
            value={form.resultado}
            onChange={(value) => onChange("resultado", value)}
            labelledBy={`${resultadoId}-label`}
            describedBy={errors.resultado ? finalizarErrorId("resultado") : undefined}
            invalid={Boolean(errors.resultado)}
          />
        </Field>
        <Field id={motivoId} errorKeyId={finalizarErrorId("motivoCierre")} label="Motivo de cierre" required full error={errors.motivoCierre}>
          <span className="crm-rollo__field crm-rollo__field--select">
            <select
              className="crm-rollo__select"
              id={motivoId}
              value={form.motivoCierre}
              disabled={!form.resultado}
              aria-required="true"
              onChange={(event) => onChange("motivoCierre", event.target.value)}
              {...invalidProps(errors.motivoCierre, finalizarErrorId("motivoCierre"))}
            >
              <option value="">{form.resultado ? "Elegí el motivo de cierre" : "Elegí primero el resultado"}</option>
              {motivos.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </span>
        </Field>
        <Field
          id={observacionId}
          errorKeyId={finalizarErrorId("observacion")}
          label="Observación"
          required={descartada}
          optional={!descartada}
          full
          error={errors.observacion}
        >
          <textarea
            className="crm-rollo__input crm-rollo__textarea"
            id={observacionId}
            rows={3}
            maxLength={OBSERVACION_MAX}
            placeholder={descartada ? "Por qué se descarta" : "Cómo se resolvió"}
            value={form.observacion}
            aria-required={descartada || undefined}
            onChange={(event) => onChange("observacion", event.target.value)}
            {...invalidProps(errors.observacion, finalizarErrorId("observacion"))}
          />
        </Field>
      </fieldset>
    </>
  );
}
